import constants from '../constants';
import { colorMarkClassName, editedElementClassName } from '../../../constants';
import * as helpers from '../../helpers';
import { TextFieldsEditableData, addStylesToEditableTextFieldsData } from '../../editableTextFields/textFieldsEditableData';

export interface IndesignTextFieldsConfig {
	page: number;
	width: number;
	height: number;
	id: string;
	parentId: string | null;
	transformStyle: string;
	content: string;
	scale: number;
	selfScale: number;
	isEditedFromBackoffice?: boolean;
}

export interface TextFieldsData {
	items: IndesignTextFieldsConfig[];
	editableData: TextFieldsEditableData | undefined;
	isError: boolean;
}

const getTextElementsFromStyles = (styles: string, innerHTML: string): HTMLSpanElement[] => {
	const stylesArray = styles.split(';').filter(style => style);

	// inDesign doesn't export text-decoration style. But it is needed for proper work of <RichTextEditor> component
	if (!styles.includes('text-decoration')) {
		stylesArray.push('text-decoration:none');
	}

	return stylesArray.map((style, index, array) => {
		const span = document.createElement('span');

		// A special class is needed for proper color of underline (managed in css)
		if (style.includes('color')) {
			span.classList.add(colorMarkClassName);
		}

		span.setAttribute('style', style);
		const isLast = index === array.length - 1;
		if (isLast) {
			span.innerHTML = innerHTML;
		}
		return span;
	});
};

const replaceTextElements = (oldElement: HTMLSpanElement, newElements: HTMLSpanElement[]): void => {
	for (let i = 1; i < newElements.length; i += 1) {
		newElements[i - 1].appendChild(newElements[i]);
	}

	oldElement.parentNode!.replaceChild(newElements[0], oldElement);
};

const getModifiedParagraphElementsStyle = (elements: HTMLParagraphElement[], scale: number): void => {
	elements.forEach((element) => {
		const styles = element.getAttribute('style');

		if (styles) {
			const modifiedStyles = helpers.getStylesWithScaledNumbers(styles, scale);
			element.setAttribute('style', modifiedStyles);
		}

		return element;
	});
};

const getModifiedTextElementsContent = (elements: HTMLSpanElement[], scale: number): void => {
	elements.forEach((element) => {
		const styles = element.getAttribute('style');

		if (styles) {
			const modifiedStyles = helpers.getStylesWithScaledNumbers(styles, scale);

			// By default Slate adds line-break symbol to each paragraph of external pasted text. It should be removed for proper display
			const innerHTML = element.innerHTML.replace(/\n/g, '');
			const textElements = getTextElementsFromStyles(modifiedStyles, innerHTML);
			replaceTextElements(element, textElements);
		}
	});
};

const getTextElementsStyles = (textElements: HTMLSpanElement[], css: string, scale: number, editableData: TextFieldsEditableData | undefined): { [k: string]: string } => (
	textElements.reduce((acc, current) => {
		const { className, classList } = current;
		// generated css for text elements has selectors like span.CharOverride-1 but text elements can have several classes. We have to remove another classes from selector
		const filteredClassName = [...classList].find(className => (
			className.includes(constants.textFieldCharacterClassName)
		));

		if (acc[className]) {
			return acc;
		}

		const styles = helpers.getSelectorStylesFromCss(`\\.${filteredClassName}`, css);

		if (styles) {
			const modifiedStyles = helpers.getStylesWithScaledNumbers(styles, scale);

			if (editableData) {
				addStylesToEditableTextFieldsData(editableData, modifiedStyles);
			}

			acc[className] = modifiedStyles;
		}

		return acc;
	}, {})
);

const getTextElementInlineStyles = (textElement: HTMLSpanElement, scale: number, editableData: TextFieldsEditableData | undefined): string => {
	const stylesToCheck = ['letter-spacing'];
	const styles = stylesToCheck
		.reduce((acc, current) => {
			const { style } = textElement;
			if (style[current]) {
				acc.push(`${current}:${style[current]};`);
			}
			return acc;
		}, [] as string[])
		.map((style) => {
			if (style.includes('letter-spacing')) {
				const value = style.split(':')[1];
				const correctedValue = helpers.getCorrectedLetterSpacing(value, scale);
				return correctedValue ? `letter-spacing:${correctedValue}px;` : '';
			}
			return style;
		})
		.join('');

	if (editableData) {
		addStylesToEditableTextFieldsData(editableData, styles);
	}

	return styles;
};

const getUnmodifiedParagraphElementsStyle = (
	parentClassName: string,
	elements: HTMLParagraphElement[],
	css: string,
	scale: number,
	editableData: TextFieldsEditableData | undefined,
): void => {
	elements.forEach((element, index) => {
		// remove all inDesign classes to avoid styles collision
		element.removeAttribute('class');

		const textFieldClassNameRegExp = new RegExp(`${constants.textFieldClassName}-\\d+_\\d+`);
		const match = parentClassName.match(textFieldClassNameRegExp);
		const textFieldClassName = match && match[0];

		if (!textFieldClassName) {
			return;
		}

		const selector = `\\[class\\*=\\"${textFieldClassName}\\"\\] p:nth-of-type\\(${index + 1}\\)`;
		const styles = helpers.getSelectorStylesFromCss(selector, css);

		if (styles) {
			const modifiedStyles = helpers.getStylesWithScaledNumbers(styles, scale);

			if (editableData) {
				addStylesToEditableTextFieldsData(editableData, modifiedStyles);
			}

			element.setAttribute('style', modifiedStyles);
		}
	});
};

const getUnmodifiedTextElementsContent = (
	elements: HTMLSpanElement[],
	css: string,
	scale: number,
	editableData: TextFieldsEditableData | undefined,
): void => {
	const textElementsStyles = getTextElementsStyles(elements, css, scale, editableData);
	let textElementInnerHtml = '';
	let isReplacementNeeded = true;

	elements.forEach((element, index, elements) => {
		const currentElement = element;
		const nextElement = elements[index + 1];

		const currentElementInlineStyles = getTextElementInlineStyles(
			currentElement,
			scale,
			editableData,
		);
		const currentElementStyles = `${textElementsStyles[currentElement.className]}${currentElementInlineStyles}`;

		let elementToReplace = currentElement;

		if (nextElement) {
			const nextElementInlineStyles = getTextElementInlineStyles(
				nextElement,
				scale,
				editableData,
			);
			const nextElementStyles = `${textElementsStyles[nextElement.className]}${nextElementInlineStyles}`;
			const isSiblings = currentElement.parentNode === nextElement.parentNode;
			const isSameStyles = currentElementStyles === nextElementStyles;

			if (isSiblings && isSameStyles) {
				isReplacementNeeded = false;
				currentElement.parentNode!.removeChild(currentElement);
				elementToReplace = nextElement;
			} else {
				isReplacementNeeded = true;
			}
		} else {
			isReplacementNeeded = true;
		}

		textElementInnerHtml += currentElement.innerHTML;

		if (isReplacementNeeded) {
			const newElements = getTextElementsFromStyles(currentElementStyles, textElementInnerHtml);
			replaceTextElements(elementToReplace, newElements);
			textElementInnerHtml = '';
		}
	});
};

const getContent = (
	parentClassName: string,
	element: HTMLDivElement,
	css: string,
	scale: number,
	isEditedFromBackoffice: boolean,
	editableData: TextFieldsEditableData | undefined,
): string => {
	const paragraphElements = [
		...element.querySelectorAll('p'),
	] as HTMLParagraphElement[];
	const textElements = [
		...element.querySelectorAll('p span'),
	] as HTMLSpanElement[];

	if (isEditedFromBackoffice) {
		getModifiedParagraphElementsStyle(paragraphElements, scale);
		getModifiedTextElementsContent(textElements, scale);
	} else {
		getUnmodifiedParagraphElementsStyle(
			parentClassName,
			paragraphElements,
			css,
			scale,
			editableData,
		);
		getUnmodifiedTextElementsContent(textElements, css, scale, editableData);
	}

	if (!isEditedFromBackoffice) {
		// We have to remove unnecessary spaces generated by inDesign for proper render in RTE
		return helpers.getStringWithoutSpaces(element.innerHTML);
	}

	return element.innerHTML;
};

// in some cases (text frame inside animated frame) we have to change some styles of text frame parent element. To achieve it we have to know parent id
const getParentId = (element: HTMLElement): string | null => {
	const elementWrapperSelector = `[id*=${constants.parentContainerId}]:not([class*=${constants.textFieldClassName}]) > div[class*=${constants.textFieldWrapperClassName}]`;
	const elementWrapper = element.closest(elementWrapperSelector);
	const elementWrapperParent = elementWrapper && elementWrapper.parentNode as HTMLElement;

	return elementWrapperParent ? elementWrapperParent.id : null;
};

const getConfig = (element: HTMLElement, page: number, css: string, editableData: TextFieldsEditableData | undefined): IndesignTextFieldsConfig => {
	const { id, className, classList } = element;
	const innerDiv = element.querySelector('div') as HTMLDivElement;
	const { style } = innerDiv;
	const { width, height } = style;
	const scale = helpers.getScaleFromStyle(style);
	const isEditedFromBackoffice = classList.contains(editedElementClassName);

	return {
		page,
		width: helpers.getNumberFromString(width as string) * scale,
		height: helpers.getNumberFromString(height as string) * scale,
		id,
		parentId: getParentId(element),
		transformStyle: helpers.getTransformStyle(style),
		content: getContent(
			className,
			innerDiv,
			css,
			scale,
			isEditedFromBackoffice,
			editableData,
		),
		scale,
		// some text fields have transform: scale property. We have to take it into account to calculate correct outline width of active text frame
		selfScale: helpers.getElementScale(element, css),
	};
};

export default (page: number, htmlDocument: HTMLDocument, css: string, isModifiedFromBackoffice: boolean): TextFieldsData => {
	const items: IndesignTextFieldsConfig[] = [];
	const editableData = isModifiedFromBackoffice
		? undefined
		: {
			fontFamilies: new Set<string>(),
			fontSizes: new Set<string>(),
			colors: new Set<string>(),
			lineHeights: new Set<string>(),
			letterSpacings: new Set<string>(),
		};
	const editableTextFieldElements = [
		...htmlDocument.querySelectorAll(`[class*=${constants.textFieldClassName}]:not([class*=${constants.lockedClassName}])`),
	] as HTMLElement[];
	let isError: boolean = false;

	try {
		editableTextFieldElements.forEach(fieldElement => (
			items.push(getConfig(fieldElement, page, css, editableData))
		));
	} catch (err) {
		isError = true;
	}

	return {
		items,
		editableData,
		isError,
	};
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/Editor/utils/indesignEditor/editableTextFields/getData.ts